import { useNavigate } from "react-router-dom";
import { LogOut, RefreshCw, Play, Moon, Sun, Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  useGetStatusQuery,
  useGetMyStatusQuery,
  useRunCycleMutation,
  useRunMyCycleMutation,
} from "@/store/api";
import { useAppDispatch, useAppSelector } from "@/store";
import { logout } from "@/store/authSlice";
import { useTheme } from "@/hooks/useTheme";
import { toast } from "sonner";

interface Props {
  onOpenMobileMenu?: () => void;
}

export function Topbar({ onOpenMobileMenu }: Props = {}) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [theme, setTheme] = useTheme();
  const isAdmin = useAppSelector((s) => s.auth.user?.role === "admin");
  // /api/status is admin-only — skip the poll entirely for regular users so
  // we don't spam 403s and we don't expose global flags they can't change.
  const { data: status, refetch, isFetching } = useGetStatusQuery(undefined, {
    pollingInterval: 10000,
    skip: !isAdmin,
  });
  const [runCycle, { isLoading: cycling }] = useRunCycleMutation();
  // For non-admins: read /api/me/status so we know whether admin allows
  // user-triggered cycles, and use the user-scoped run-cycle mutation.
  const myStatus = useGetMyStatusQuery(undefined, {
    pollingInterval: 30000,
    skip: isAdmin,
  });
  const [runMyCycle, { isLoading: cyclingMine }] = useRunMyCycleMutation();
  const userCanRun = !isAdmin && !!myStatus.data?.users_can_run_cycle;

  const onSignOut = () => {
    dispatch(logout());
    navigate("/login");
  };

  const onRunCycle = async () => {
    try {
      await runCycle().unwrap();
      toast.success("Cycle complete");
    } catch (e) {
      const msg = (e as { data?: { detail?: string } })?.data?.detail ?? "Cycle failed";
      toast.error(msg);
    }
  };

  const onRunMyCycle = async () => {
    try {
      const res = await runMyCycle().unwrap();
      toast.success(`Cycle #${res.cycle_id} queued — your row will appear in Cycles shortly.`);
    } catch (e) {
      const msg = (e as { data?: { detail?: string } })?.data?.detail ?? "Cycle trigger failed";
      toast.error(msg);
    }
  };

  const settings = (status?.settings ?? {}) as Record<string, unknown>;
  const dryRun = settings.dry_run as boolean | undefined;
  const approval = settings.approval_mode as "manual" | "auto" | undefined;
  const paused = settings.paused as boolean | undefined;

  return (
    <header className="h-14 shrink-0 border-b border-border bg-bg2 flex items-center px-3 sm:px-4 gap-2">
      {onOpenMobileMenu && (
        <Button
          variant="ghost"
          size="sm"
          className="md:hidden -ml-1"
          onClick={onOpenMobileMenu}
          title="Menu"
          aria-label="Open menu"
        >
          <Menu className="w-4 h-4" />
        </Button>
      )}
      <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
        {isAdmin && dryRun !== undefined && (
          <Badge tone={dryRun ? "warn" : "bad"}>{dryRun ? "DRY RUN" : "LIVE"}</Badge>
        )}
        {isAdmin && approval && (
          <Badge tone={approval === "auto" ? "info" : "default"}>{approval}</Badge>
        )}
        {isAdmin && paused !== undefined && (
          <Badge tone={paused ? "warn" : "good"}>
            {paused ? "paused" : "running"}
          </Badge>
        )}
      </div>
      <div className="flex-1" />
      {isAdmin && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => refetch()}
          disabled={isFetching}
          title="Refresh (R)"
        >
          <RefreshCw className={"w-4 h-4 " + (isFetching ? "animate-spin" : "")} />
        </Button>
      )}
      {isAdmin && (
        <Button
          variant="primary"
          size="sm"
          onClick={onRunCycle}
          disabled={cycling}
          title="Run a cycle now"
        >
          <Play className="w-4 h-4" />
          <span className="hidden sm:inline">{cycling ? "Running…" : "Run cycle"}</span>
        </Button>
      )}
      {userCanRun && (
        <Button
          variant="primary"
          size="sm"
          onClick={onRunMyCycle}
          disabled={cyclingMine}
          title="Trigger a fresh cycle on your account"
        >
          <Play className="w-4 h-4" />
          <span className="hidden sm:inline">{cyclingMine ? "Running…" : "Run cycle"}</span>
        </Button>
      )}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
        title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
        aria-label="Toggle theme"
      >
        {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
      </Button>
      <Button variant="ghost" size="sm" onClick={onSignOut} title="Sign out">
        <LogOut className="w-4 h-4" />
      </Button>
    </header>
  );
}
